import { jsx as _jsx } from "react/jsx-runtime";
import { ObjectTree } from '../../../v2/components/generic/ObjectTree';
import { NavigationSkeleton } from '../../common/NavigationSkeleton';
import { getCustomerPropertyAreaPlacePath } from '../TreeDefinitions/CustomerPropertyAreaPlaceTree';
import { getCustomerDeviceGroupStep } from '../TreeDefinitions/DeviceGroups';
import { addExternalDomainTreesToTreeDefinitionMap } from './addExternalDomainTrees';
import { getTopLevelSectionDefintions } from './defaultMenuStructure';
import { sortTreeDefinition } from './menuSorter';
import { mergeExternalTreeDefinitionMapInput } from './mergeExternalTree';
import { pruneTreeByDefinition, pruneTreeByPathMap } from './treePruning';
const debug = process.env.REACT_APP_DEBUG && false;
export const getGeneratedTreePart = ({ domainTreeMapping, treeDefinitionMap }) => {
    const x = domainTreeMapping.reduce((a, c) => {
        debug && console.log(`getGeneratedTreePart ${c.domainKey}`, JSON.stringify(treeDefinitionMap));
        return addExternalDomainTreesToTreeDefinitionMap({ externalDomainMapping: c, treeDefinitionMap: a });
    }, treeDefinitionMap);
    return x;
};
/** creates a Customer > [queryPath] tree for one query path and a given domain */
const domainObjectTreeMapping = ({ domainKey, sortOrder, targetSectionPath, queryPaths }) => {
    const mapping = {
        domainKey,
        sortOrder,
        targetSectionPath,
        treeDefMap: {
            ObjectTree: {
                treeFC: ({ applicationData, contextCustomer }) => {
                    return contextCustomer ? (_jsx(ObjectTree, { uniqueTreeId: `${targetSectionPath.join('_')}_${domainKey}_`,
                        maintree_rightclick: true,
                        hideTopStep: true,
                        contextObjects: [contextCustomer],
                        nodeEdgeMapper: applicationData,
                        queryPaths }, `${targetSectionPath.join('_')}_${domainKey}_`)) : (_jsx(NavigationSkeleton, {}, `${targetSectionPath.join('_')}_${domainKey}_`));
                },
            },
        },
    };
    return mapping;
};
/** creates a Device Group > Device tree for each domain, filtered such that only Device Groups which are set to the domain are shown */
const domainDeviceGroupsTreeMapping = ({ domainMap, targetSectionPath }) => {
    const mappings = Object.entries(domainMap).map(([k, { defaultSortOrder }]) => {
        const queryPaths = [
            getCustomerDeviceGroupStep({
                filters: [(vob) => vob.domain === k],
            }),
        ];
        const mapping = domainObjectTreeMapping({ domainKey: k, queryPaths, sortOrder: defaultSortOrder, targetSectionPath });
        return mapping;
    });
    debug && console.log('domainDeviceGroupsTreeMapping', { domainMap, targetSectionPath, mappings });
    if (Object.keys(domainMap).length > 0) {
        const unClassfiedQueryPath = [
            getCustomerDeviceGroupStep({
                filters: Object.keys(domainMap).map((k) => (vob) => vob.domain !== k),
            }),
        ];
        const unclassifiedMapping = domainObjectTreeMapping({
            domainKey: 'Unclassified',
            queryPaths: unClassfiedQueryPath,
            sortOrder: 100,
            targetSectionPath,
        });
        mappings.push(unclassifiedMapping);
    }
    else {
        debug && console.log(`No domains, skipping unclassifieds`);
    }
    return mappings;
};
/** creates a Property Group > Property tree for each domain, filtered such that only Property Groups which are set to the domain are shown */
const domainPropertyGroupsTreeMapping = ({ domainMap, targetSectionPath }) => {
    const mappings = Object.entries(domainMap).map(([k, { defaultSortOrder }]) => {
        const queryPaths = [
            getCustomerPropertyAreaPlacePath({
                filters: [(vob) => vob.domain === k],
            }),
        ];
        const mapping = domainObjectTreeMapping({ domainKey: k, queryPaths, sortOrder: defaultSortOrder, targetSectionPath });
        return mapping;
    });
    if (Object.keys(domainMap).length > 0) {
        const unClassfiedQueryPath = [
            getCustomerPropertyAreaPlacePath({
                filters: Object.keys(domainMap).map((k) => (vob) => vob.domain !== k),
            }),
        ];
        const unClassifiedMapping = domainObjectTreeMapping({
            domainKey: 'Unclassified',
            queryPaths: unClassfiedQueryPath,
            sortOrder: 100,
            targetSectionPath,
        });
        mappings.push(unClassifiedMapping);
    }
    else {
        debug && console.log(`No domains, skipping unclassifieds`);
    }
    return mappings;
};
/** given input which will determine component visibility and allowed actions, returns a configuration to be instantiated with standard props */
// TODO:
export const viewArbitrator = ({ domainMap, domainTreeMapping, externalTreeDefinitionMap, prunePathMap, externalMenuOrdering, }) => {
    // Merge external tree def with standard tree def to get base tree. Note that external tree def will be null if we have no venture provided tree but do have domains */
    const mergedTreeDefinition = mergeExternalTreeDefinitionMapInput({ externalTreeDefinitionMap, treeDefinitionMap: getTopLevelSectionDefintions() });
    /** Add domains to tree */
    let domainTreeAdditions = [];
    if (domainTreeMapping && domainMap) {
        // Start with all venture specified domain trees
        domainTreeAdditions = domainTreeMapping;
        // Add special domain device groups - tree of device groups that have the specified domain
        // Will be inserted into following paths e.g. SetUp > Devices > {Domain} > {Generated Device Group Tree}
        // const targetDeviceTreePaths: string[][] = [
        //   ['SetUp', 'Devices'],
        //   ['Admin', 'Devices'],
        // ]
        // targetDeviceTreePaths.forEach((treePath) => {
        //   const domainDeviceGroupAdditions = domainDeviceGroupsTreeMapping({ targetSectionPath: treePath, domainMap })
        //   domainTreeAdditions = domainTreeAdditions.concat(domainDeviceGroupAdditions)
        // })
        // Add Special Domain property groups
        // INFO: "Places" is the key in the default menu that actually renders a tree of property -> area -> place
        const targetPropertyTreePaths = [
            ['SetUp', 'Places', 'Properties'],
            ['Admin', 'Places', 'Properties'],
        ];
        targetPropertyTreePaths.forEach((treePath) => {
            const domainPropertyAdditions = domainPropertyGroupsTreeMapping({ targetSectionPath: treePath, domainMap });
            domainTreeAdditions = domainTreeAdditions.concat(domainPropertyAdditions);
        });
    }
    const generatedTreePart = getGeneratedTreePart({ domainTreeMapping: domainTreeAdditions, treeDefinitionMap: mergedTreeDefinition });
    const definitionPrunedTree = pruneTreeByDefinition({ treeStructureDefinitionMap: generatedTreePart });
    const raiPrunedTree = pruneTreeByPathMap({ treeStructureDefinitionMap: definitionPrunedTree, prunePathMap });
    // HERE - should sort the output tree by some sort of sort definition
    /**
     * 24 July 2025 ADH - In an ideal world, the domain map and external tree def would be one object that you merge into the default tree map, then prune as desired. If you need to have some illusion that domains are more core to LSC than venture-specified menu additions then you could have it as a seperate merge step but there's no reason the mechanism couldn't be the same as a normal tree def merge.
     *
     * At the moment, domain mapping does it's own thing and kind of sorts within itself, but the sort order code is difficult to parse and I don't technically have time to even be doing the work
     * I've been doing on the menu in the first place, let alone refactoring the menu system. What I know is: the external tree map + pruning is technically all you would need to achieve the results that
     * the current domain tree mapping + external tree def achieve. I don't particularly like having another external structure resonsible for sorting the tree, but since I lack the time to marry
     * the domain map and external tree def merge process at this stage, it will suffice.
     */
    let sortedPrunedTree = null;
    if (externalMenuOrdering) {
        sortedPrunedTree = sortTreeDefinition(raiPrunedTree, externalMenuOrdering);
    }
    return { treeDefinitionMap: sortedPrunedTree ?? raiPrunedTree };
};
